import type { OperatorDetail } from '../../../../arcticapi/types';
import { getTransportModeIcon } from '../../../../transportModes';
import { SvgIcon } from '../../../base/Icon/SvgIcon';
import { Link } from '../../../base/Link/Link';
import styles from './JourneyPlanOperatorDetails.module.css';

interface JourneyPlanOperatorDetailsProps {
  mode: string;
  operator: OperatorDetail;
}

interface OperatorField {
  label: string;
  value: string;
  href?: string;
  isExternal?: boolean;
}

/**
 * Format the telephone number
 * @param tel
 * @returns
 */
const formatTelephone = (tel: string) => {
  const cleaned = tel.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{5})(\d{3})(\d{3})$/);
  if (match) {
    return `${match[1]} ${match[2]} ${match[3]}`;
  }
  return tel; // Fallback if it doesn't match the pattern
};

function normaliseWebsiteHref(value: string): string {
  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `https://${value}`;
}

function toOperatorFields(operator: OperatorDetail): OperatorField[] {
  const attributes = operator.attributes;

  if (!attributes) {
    return [];
  }

  const fields: Array<OperatorField | undefined> = [
    attributes.phone
      ? {
          label: 'Telephone',
          value: formatTelephone(attributes.phone),
          href: `tel:${attributes.phone.replace(/\s+/g, '')}`,
        }
      : undefined,
    attributes.address
      ? {
          label: 'Address',
          value: attributes.address,
        }
      : undefined,
    attributes.email
      ? {
          label: 'Email',
          value: attributes.email,
          href: `mailto:${attributes.email}`,
        }
      : undefined,
    attributes.website
      ? {
          label: 'Website',
          value: attributes.website,
          href: normaliseWebsiteHref(attributes.website),
          isExternal: true,
        }
      : undefined,
    attributes.twitter
      ? {
          label: 'X (formerly Twitter)',
          value: attributes.twitter,
          href: /^https?:\/\//i.test(attributes.twitter)
            ? attributes.twitter
            : `https://x.com/${attributes.twitter.replace(/^@/, '')}`,
          isExternal: true,
        }
      : undefined,
  ];

  return fields.filter((field): field is OperatorField => Boolean(field));
}

export function JourneyPlanOperatorDetails({
  mode,
  operator,
}: JourneyPlanOperatorDetailsProps) {
  const modeIcon = getTransportModeIcon(mode);
  const fields = toOperatorFields(operator);

  return (
    <details className={styles.details}>
      <summary className={styles.summary}>
        <span className={styles.summaryContent}>
          {modeIcon ? (
            <span className={styles.iconWrap} aria-hidden="true">
              <SvgIcon
                name={modeIcon}
                size={24}
                strokeWidth="0"
                fill="currentColor"
              />
            </span>
          ) : null}
          <span className={`${styles.title} ${styles.linkText}`}>
            {operator.name}
          </span>
        </span>
        <SvgIcon
          name="chevronDown"
          size={14}
          strokeWidth="0"
          fill="black"
          className={styles.chevron}
        />
      </summary>
      <div className={styles.panel}>
        {fields.length > 0 ? (
          <dl className={styles.list}>
            {fields.map((field) => (
              <div key={field.label} className={styles.item}>
                <dt className={styles.term}>{field.label}</dt>
                <dd className={styles.description}>
                  {field.href ? (
                    <Link href={field.href} isExternal={field.isExternal}>
                      {field.value}
                    </Link>
                  ) : (
                    field.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className={styles.empty}>Operator contact details unavailable.</p>
        )}
      </div>
    </details>
  );
}
